import React from 'react';
import { Card, Typography, Button, Space, Progress, Row, Col, theme } from 'antd';
import {
  RocketOutlined,
  CloseOutlined,
  CheckCircleFilled,
  ToolOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  ArrowRightOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

export interface OnboardingCardProps {
  equipmentCount: number;
  scheduleCount: number;
  locationCount: number;
  onDismiss: () => void;
}

interface OnboardingStep {
  key: string;
  label: string;
  icon: React.ReactNode;
  completed: boolean;
  count: number;
  required: boolean;
  href: string;
  color: string;
  bgColor: string;
}

/**
 * はじめてガイドカード
 * 新規ユーザーに初期セットアップの進捗を表示し、次のアクションを誘導する
 */
export const OnboardingCard: React.FC<OnboardingCardProps> = ({
  equipmentCount,
  scheduleCount,
  locationCount,
  onDismiss,
}) => {
  const navigate = useNavigate();
  const { token } = theme.useToken();

  // ステップの定義
  const steps: OnboardingStep[] = [
    {
      key: 'equipment',
      label: '設備を登録する',
      icon: <ToolOutlined />,
      completed: equipmentCount > 0,
      count: equipmentCount,
      required: true,
      href: '/app/equipments',
      color: token.colorPrimary,
      bgColor: token.colorPrimaryBg,
    },
    {
      key: 'schedule',
      label: 'スケジュールを作成する',
      icon: <CalendarOutlined />,
      completed: scheduleCount > 0,
      count: scheduleCount,
      required: true,
      href: '/app/maintenance',
      color: token.colorPrimary,
      bgColor: token.colorPrimaryBg,
    },
    {
      key: 'location',
      label: 'エリアを登録する',
      icon: <EnvironmentOutlined />,
      completed: locationCount > 0,
      count: locationCount,
      required: false,
      href: '/app/locations',
      color: token.colorWarning,
      bgColor: token.colorWarningBg,
    },
  ];

  // 必須ステップと推奨ステップに分離
  const requiredSteps = steps.filter((s) => s.required);
  const optionalSteps = steps.filter((s) => !s.required);

  // 進捗計算（必須ステップのみ）
  const completedRequired = requiredSteps.filter((s) => s.completed).length;
  const progressPercent = Math.round((completedRequired / requiredSteps.length) * 100);

  // 次のアクションを取得
  const getNextAction = (): OnboardingStep | null => {
    // 必須ステップで未完了のものを優先
    const nextRequired = requiredSteps.find((s) => !s.completed);
    if (nextRequired) return nextRequired;

    // 全て完了していればnull
    return null;
  };

  const nextAction = getNextAction();

  // ステップカードのレンダリング（番号と矢印付き）
  const renderStepCard = (step: OnboardingStep, index: number, total: number) => {
    const statusText = step.completed
      ? `${step.count}件登録済み`
      : 'まだ登録されていません';
    const stepNumber = index + 1;
    const isLast = index === total - 1;

    return (
      <React.Fragment key={step.key}>
        <Col xs={24} sm={isLast ? 8 : 7}>
          <Card
            size="small"
            hoverable
            onClick={() => navigate(step.href)}
            style={{
              height: '100%',
              background: step.completed
                ? token.colorSuccessBg
                : step.bgColor,
              border: step.completed ? `1px solid ${token.colorSuccessBorder}` : `1px solid ${step.color}40`,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              position: 'relative',
            }}
            styles={{
              body: { padding: '16px' },
            }}
          >
            {/* ステップ番号バッジ */}
            <div
              style={{
                position: 'absolute',
                top: -8,
                left: -8,
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: step.completed ? token.colorSuccess : step.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: 12,
                fontWeight: 700,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              {stepNumber}
            </div>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: step.completed ? token.colorSuccess : step.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 10px',
                  boxShadow: `0 3px 8px ${step.completed ? token.colorSuccess : step.color}40`,
                }}
              >
                {step.completed ? (
                  <CheckCircleFilled style={{ color: 'white', fontSize: 22 }} />
                ) : (
                  <span style={{ color: 'white', fontSize: 18 }}>{step.icon}</span>
                )}
              </div>
              <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>
                {step.label}
              </Text>
              <Text
                type="secondary"
                style={{
                  fontSize: 11,
                  color: step.completed ? token.colorSuccess : undefined,
                }}
              >
                {statusText}
              </Text>
            </div>
          </Card>
        </Col>
        {/* 矢印 (最後のステップ以外) */}
        {!isLast && (
          <Col
            xs={0}
            sm={1}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ArrowRightOutlined
              style={{
                fontSize: 18,
                color: token.colorTextQuaternary,
              }}
            />
          </Col>
        )}
      </React.Fragment>
    );
  };

  return (
    <Card
      style={{
        marginBottom: 16,
        background: token.colorInfoBg,
        border: `1px solid ${token.colorInfoBorder}`,
        borderRadius: 12,
        overflow: 'hidden',
      }}
      styles={{
        body: { padding: '20px 24px' },
      }}
    >
      {/* ヘッダー */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 16,
        }}
      >
        <Space size="middle">
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: token.colorPrimary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RocketOutlined style={{ fontSize: 22, color: 'white' }} />
          </div>
          <div>
            <Title level={5} style={{ margin: 0 }}>
              はじめてガイド
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              一元帳を使い始めるための基本ステップです
            </Text>
          </div>
        </Space>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onDismiss}
          style={{ color: token.colorTextTertiary }}
        >
          非表示
        </Button>
      </div>

      {/* 進捗バー */}
      <div
        style={{
          marginBottom: 16,
          padding: '10px 14px',
          background: token.colorBgElevated,
          borderRadius: 8,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 6,
          }}
        >
          <Text style={{ fontSize: 13, fontWeight: 500 }}>
            セットアップ進捗
          </Text>
          <Text style={{ fontSize: 13, fontWeight: 600, color: token.colorPrimary }}>
            {completedRequired}/{requiredSteps.length} 完了
          </Text>
        </div>
        <Progress
          percent={progressPercent}
          showInfo={false}
          strokeColor={token.colorSuccess}
          trailColor={token.colorFillSecondary}
          size={[null, 6]}
        />
      </div>

      {/* 必須ステップ */}
      <div style={{ marginBottom: 12 }}>
        <Text style={{ fontSize: 12, fontWeight: 600, color: token.colorTextSecondary, marginBottom: 10, display: 'block' }}>
          必須ステップ
        </Text>
        <Row gutter={[8, 12]} align="middle">
          {requiredSteps.map((step, index) => renderStepCard(step, index, requiredSteps.length))}
        </Row>
      </div>

      {/* 推奨ステップ */}
      {optionalSteps.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 12, fontWeight: 600, color: token.colorTextSecondary, marginBottom: 10, display: 'block' }}>
            推奨ステップ（任意）
          </Text>
          <Row gutter={[8, 12]} align="middle">
            {optionalSteps.map((step, index) => renderStepCard(step, index, optionalSteps.length))}
          </Row>
        </div>
      )}

      {/* 次のアクションボタン */}
      {nextAction && (
        <Button
          type="primary"
          size="large"
          icon={<ArrowRightOutlined />}
          onClick={() => navigate(nextAction.href)}
          style={{
            width: '100%',
            height: 44,
            borderRadius: 8,
            fontWeight: 500,
          }}
        >
          次のステップ: {nextAction.label}
        </Button>
      )}

      {/* 全て完了時 */}
      {!nextAction && (
        <div
          style={{
            textAlign: 'center',
            padding: '14px 16px',
            background: token.colorSuccessBg,
            borderRadius: 8,
            border: `1px solid ${token.colorSuccessBorder}`,
          }}
        >
          <TrophyOutlined
            style={{ color: token.colorWarning, fontSize: 22, marginRight: 8 }}
          />
          <CheckCircleFilled
            style={{ color: token.colorSuccess, fontSize: 22, marginRight: 10 }}
          />
          <Text style={{ color: token.colorSuccess, fontWeight: 600, fontSize: 15 }}>
            基本セットアップが完了しました！
          </Text>
        </div>
      )}
    </Card>
  );
};

export default OnboardingCard;
